import {
	extractProviderIdFromErrorText,
	hasProviderAuthorizationError,
} from "@llmgateway/shared";

import { getErrorMessage, getErrorStatus } from "./utils";

import type { Organization } from "./types";

type Translate = (
	key: string,
	params?: Record<string, string | number>,
) => string;

interface ProviderLabelDefinition {
	fallback: string;
	translationKey?: string;
}

interface ContextualChatErrorOptions {
	error: unknown;
	organizations: Organization[];
	selectedOrganization: Organization | null;
	chatOrganization: Organization | null;
	t: Translate;
}

const PROVIDER_LABELS: Record<string, ProviderLabelDefinition> = {
	alibaba: {
		fallback: "Alibaba Bailian",
		translationKey: "chatPage.providerLabelAlibaba",
	},
	anthropic: { fallback: "Anthropic" },
	azure: { fallback: "Azure OpenAI" },
	"azure-openai": { fallback: "Azure OpenAI" },
	bedrock: { fallback: "AWS Bedrock" },
	deepseek: { fallback: "DeepSeek" },
	fireworks: { fallback: "Fireworks" },
	gemini: { fallback: "Google Gemini" },
	google: { fallback: "Google" },
	"google-ai-studio": { fallback: "Google AI Studio" },
	"google-vertex": { fallback: "Google Vertex AI" },
	groq: { fallback: "Groq" },
	minimax: { fallback: "MiniMax" },
	mistral: { fallback: "Mistral" },
	openai: { fallback: "OpenAI" },
	openrouter: { fallback: "OpenRouter" },
	perplexity: { fallback: "Perplexity" },
	together: { fallback: "Together AI" },
	vertex: { fallback: "Google Vertex AI" },
	xai: { fallback: "xAI" },
};

function extractInsufficientCreditsOrganizationId(
	message: string,
): string | null {
	const match = message.match(
		/Organization\s+([A-Za-z0-9_-]+)\s+has insufficient credits/i,
	);
	return match?.[1] ?? null;
}

function isInsufficientCreditsMessage(message: string): boolean {
	return /insufficient credits|额度不足|余额不足/i.test(message);
}

function getProviderLabel(message: string, t: Translate): string | null {
	const providerId = extractProviderIdFromErrorText(message);
	if (!providerId) {
		return null;
	}

	const definition = PROVIDER_LABELS[providerId];
	if (!definition) {
		return providerId;
	}

	if (definition.translationKey) {
		return t(definition.translationKey);
	}

	return definition.fallback;
}

export function getContextualChatErrorMessage({
	error,
	organizations,
	selectedOrganization,
	chatOrganization,
	t,
}: ContextualChatErrorOptions): string {
	const rawMessage = getErrorMessage(error);

	if (hasProviderAuthorizationError(rawMessage)) {
		const provider = getProviderLabel(rawMessage, t);
		return provider
			? t("chatPage.providerAuthorizationFailed", { provider })
			: t("chatPage.providerAuthorizationFailedGeneric");
	}

	if (
		getErrorStatus(error) !== 402 ||
		!isInsufficientCreditsMessage(rawMessage)
	) {
		return rawMessage;
	}

	const insufficientOrgId =
		extractInsufficientCreditsOrganizationId(rawMessage);
	const fundedOrganization =
		organizations.find((organization) => Number(organization.credits) > 0) ??
		null;

	if (
		chatOrganization &&
		insufficientOrgId === chatOrganization.id &&
		fundedOrganization
	) {
		return t("chatPage.chatContextInsufficientCreditsWithOrganization", {
			organization: fundedOrganization.name,
		});
	}

	if (
		selectedOrganization &&
		(!insufficientOrgId || insufficientOrgId === selectedOrganization.id)
	) {
		return t("chatPage.organizationInsufficientCredits", {
			organization: selectedOrganization.name,
		});
	}

	if (chatOrganization && insufficientOrgId === chatOrganization.id) {
		return t("chatPage.chatContextInsufficientCredits");
	}

	return rawMessage;
}
